import { DefaultNamespace, EnumKey, FlatNamespace, KeyPrefix, ParseKeys, TOptions } from '@if/core/i18n'
import translate from '@src/services/translate'
import { translateErrorCode } from '@src/types'
import { DetailedHTMLProps, forwardRef, InputHTMLAttributes, Ref, useMemo, type JSX } from 'react'
import { FieldError } from 'react-hook-form'
import { FallbackNs, useTranslation } from 'react-i18next'

export const useTranslatedOptionEnum = <
  NS extends FlatNamespace = DefaultNamespace,
  KPrefix extends KeyPrefix<FallbackNs<NS>> = KeyPrefix<FallbackNs<NS>>,
  Key = string | number,
>(
  prefix: KPrefix,
  enumList: EnumKey<KPrefix, Key, NS>[],
  namespace?: NS,
): JSX.Element[] => {
  const { t } = useTranslation(namespace, { keyPrefix: prefix })
  return useMemo(
    () =>
      enumList.map((s) => (
        <option key={s as string} value={s as string}>
          {t(s as never) as string}
        </option>
      )),
    [enumList, t],
  )
}

type Props = DetailedHTMLProps<InputHTMLAttributes<HTMLSelectElement>, HTMLSelectElement> & {
  id: string
  label: ParseKeys
  error?: FieldError
  errorParams?: TOptions
  hasEmptyValue?: boolean
  disableEmptyValue?: boolean
  options: JSX.Element[]
}

const SelectInput = forwardRef<Props, Props>(
  (
    { id, error, label, errorParams, options, hasEmptyValue = false, disableEmptyValue = false, ...otherProps }: Props,
    ref,
  ) => {
    return (
      <div className={`control-group ${error && 'error'}`}>
        <label htmlFor={id}>{translate(label)}</label>
        <div className={`dropdown-wrap ${error && 'error'}`}>
          <select id={id} {...otherProps} ref={ref as Ref<HTMLSelectElement>}>
            {hasEmptyValue && (
              <option value="" disabled={disableEmptyValue}>
                {' '}
              </option>
            )}
            {options}
          </select>
        </div>
        {error && <p className="error-message">{translateErrorCode(error, errorParams)}</p>}
      </div>
    )
  },
)

SelectInput.displayName = 'SelectInput'

export default SelectInput
